/*
 * The body map.
 *
 * Tap where the cream went on, or where the patch was stuck. Sites used recently are marked, so
 * a carer putting a patch on can see at a glance which skin is still resting.
 *
 * Left and right are the resident's, not the reader's, so the left arm is drawn on the right of
 * the picture, as it would be if you were facing them.
 */
export type SiteUse = { site: string; label: string; on: string; medicine: string; patch: boolean; daysAgo: number };

type Spot = { id: string; x: number; y: number; r?: number };

const FRONT: Spot[] = [
  { id: 'chest-right', x: 82, y: 96 },
  { id: 'chest-left', x: 118, y: 96 },
  { id: 'abdomen-right', x: 82, y: 142 },
  { id: 'abdomen-left', x: 118, y: 142 },
  { id: 'arm-upper-right', x: 47, y: 104 },
  { id: 'arm-upper-left', x: 153, y: 104 },
  { id: 'forearm-right', x: 41, y: 154 },
  { id: 'forearm-left', x: 159, y: 154 },
  { id: 'hand-right', x: 38, y: 199, r: 11 },
  { id: 'hand-left', x: 162, y: 199, r: 11 },
  { id: 'thigh-right', x: 83, y: 240 },
  { id: 'thigh-left', x: 117, y: 240 },
  { id: 'shin-right', x: 83, y: 320 },
  { id: 'shin-left', x: 117, y: 320 },
  { id: 'foot-right', x: 83, y: 386, r: 11 },
  { id: 'foot-left', x: 117, y: 386, r: 11 },
];

const BACK: Spot[] = [
  { id: 'back-upper-right', x: 82, y: 100 },
  { id: 'back-upper-left', x: 118, y: 100 },
  { id: 'back-lower-right', x: 82, y: 150 },
  { id: 'back-lower-left', x: 118, y: 150 },
  { id: 'sacrum', x: 100, y: 182, r: 12 },
  { id: 'buttock-right', x: 83, y: 212 },
  { id: 'buttock-left', x: 117, y: 212 },
  { id: 'calf-right', x: 83, y: 320 },
  { id: 'calf-left', x: 117, y: 320 },
];

export function BodyMap({
  sites,
  value,
  onChange,
  recent,
  restDays,
}: {
  sites: Array<{ id: string; label: string; side: 'front' | 'back' }>;
  value: string;
  onChange: (site: string) => void;
  recent: SiteUse[];
  restDays: number;
}) {
  const labelOf = (id: string) => sites.find((s) => s.id === id)?.label ?? id;
  const usedRecently = (id: string) =>
    recent.filter((use) => use.site === id && use.patch).sort((a, b) => a.daysAgo - b.daysAgo)[0];

  return (
    <div className="bodymap">
      {[
        { side: 'front' as const, spots: FRONT, title: 'Front' },
        { side: 'back' as const, spots: BACK, title: 'Back' },
      ].map(({ side, spots, title }) => (
        <figure key={side}>
          <figcaption>{title}</figcaption>
          <svg viewBox="0 0 200 420" role="img" aria-label={`${title} of the body, tap where it went`}>
            <g className="body">
              <circle cx="100" cy="40" r="26" />
              <rect x="92" y="62" width="16" height="12" />
              <rect x="64" y="72" width="72" height="120" rx="16" />
              <rect x="34" y="78" width="24" height="112" rx="12" />
              <rect x="142" y="78" width="24" height="112" rx="12" />
              <rect x="30" y="186" width="20" height="26" rx="8" />
              <rect x="150" y="186" width="20" height="26" rx="8" />
              <rect x="68" y="190" width="28" height="170" rx="13" />
              <rect x="104" y="190" width="28" height="170" rx="13" />
              <rect x="66" y="374" width="32" height="20" rx="8" />
              <rect x="102" y="374" width="32" height="20" rx="8" />
            </g>

            {spots.map((spot) => {
              const used = usedRecently(spot.id);
              const resting = used && used.daysAgo < restDays;
              const chosen = value === spot.id;
              return (
                <g key={spot.id} className={`spot ${chosen ? 'on' : ''} ${resting ? 'resting' : ''}`}>
                  <circle
                    cx={spot.x}
                    cy={spot.y}
                    r={spot.r ?? 14}
                    tabIndex={0}
                    role="button"
                    aria-label={`${labelOf(spot.id)}${resting ? `, used ${used.daysAgo} days ago` : ''}`}
                    aria-pressed={chosen}
                    onClick={() => onChange(spot.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') onChange(spot.id);
                    }}
                  />
                  {resting ? (
                    <text x={spot.x} y={spot.y + 4} textAnchor="middle" className="spot-days">
                      {used.daysAgo}d
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>
        </figure>
      ))}

      <p className="muted small map-note">
        {value ? (
          <strong>{labelOf(value)}</strong>
        ) : (
          'Tap where it went. Left and right are the resident\'s.'
        )}
        {recent.some((use) => use.patch) ? ' Circled numbers are days since a patch was last there.' : ''}
      </p>
    </div>
  );
}
